import { appendQueryParam } from '@zorro/common-utils';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import {
  Anchor,
  Box,
  Center,
  Group,
  Icon,
  Text,
} from '@zorro/web/design-system';
import {
  useDynamicRouter,
  useImpersonation,
  useMobileDetector,
} from '@zorro/web/ui-utils';
import React, { ElementType, ReactNode } from 'react';

import { useAnalytics } from '../../Analytics';
import { useAppLayout } from '../AppLayoutContext';
import classNames from './AppLayoutSidebarItem.module.scss';
import { AppLayoutSidebarItemSkeleton } from './AppLayoutSidebarItemSkeleton';
import { isSidebarLinkActive } from './AppLayoutSidebarItemUtils';

export type AppLayoutSidebarItemProps = {
  link: string;
  label: string;
  icon: ElementType;
  isLoading?: boolean;
  isDisabled?: boolean;
  isHidden?: boolean;
  rightSection?: ReactNode;
};

export const AppLayoutSidebarItem = ({
  label,
  icon,
  link: linkProp,
  isLoading = false,
  isDisabled = false,
  isHidden = false,
  rightSection,
}: AppLayoutSidebarItemProps) => {
  const { isSidebarOpen, toggleSidebar } = useAppLayout();
  const { employee, isImpersonated } = useImpersonation();
  const { isMobile } = useMobileDetector();
  const { router } = useDynamicRouter({});
  const { sendEvent } = useAnalytics();

  if (isLoading) {
    return <AppLayoutSidebarItemSkeleton />;
  }

  const link = isImpersonated
    ? appendQueryParam(linkProp, 'impersonatedEmployeeId', employee?.id || '')
    : linkProp;

  const isChecked = isSidebarLinkActive(router.asPath, linkProp);

  const handleClick = (evt: React.MouseEvent) => {
    if (
      evt.metaKey || // Cmd on Mac
      evt.ctrlKey || // Ctrl on Windows/Linux
      evt.button === 1 // Middle mouse button
    ) {
      return;
    }

    evt.preventDefault(); // Prevent default anchor behavior
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.push(link);
    if (isMobile) {
      toggleSidebar();
    }
    if (label === 'Payroll') {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      sendEvent('click_payroll', {
        source_path: router.asPath,
      });
    }
  };

  return (
    <Tooltip disabled={isSidebarOpen} position="right" label={label}>
      {isDisabled ? (
        <Group
          display={isHidden ? 'none' : 'flex'}
          data-opened={isSidebarOpen}
          data-disabled={isDisabled}
          data-checked={isChecked}
          classNames={classNames}
          onClick={handleClick}
        >
          <Center w={22}>
            <Icon icon={icon} />
          </Center>
          {isSidebarOpen && <Text>{label}</Text>}
          {isSidebarOpen && rightSection && <Box ml="auto">{rightSection}</Box>}
        </Group>
      ) : (
        <Anchor
          href={link}
          onClick={handleClick}
          style={{
            textDecoration: 'none',
            display: isHidden ? 'none' : 'flex',
          }}
        >
          <Group
            data-opened={isSidebarOpen}
            data-disabled={isDisabled}
            data-checked={isChecked}
            classNames={classNames}
          >
            <Center w={22}>
              <Icon icon={icon} />
            </Center>
            {isSidebarOpen && <Text>{label}</Text>}
            {isSidebarOpen && rightSection && (
              <Box ml="auto">{rightSection}</Box>
            )}
          </Group>
        </Anchor>
      )}
    </Tooltip>
  );
};
